import { Typography } from "@mui/material";
import { DeliveryStatus } from "@types_def/models/delivery.types";
import { getDeliveryTypeColor } from "@utils/functions/Delivery/DeliveryType/deliveryTypeAttributes";
import { useMap } from "@vis.gl/react-google-maps";
import { motion } from "framer-motion";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import CustomMarker from "./Marker";
import type { Mission } from "./type";
import { PreviewType } from "./type";
import { addRoutes } from "./utils";

type Props = {
  mission: Mission;
  previewType: PreviewType;
};

function PreviewMissionMarkers({ mission, previewType }: Props) {
  const { t } = useTranslation();
  const map = useMap();

  const routeEffectKey = useMemo(
    () =>
      JSON.stringify({
        previewType,
        id: mission.id,
        type: mission.type,
        status: mission.status,
        oLat: mission.origin?.lat,
        oLng: mission.origin?.lng,
        dLat: mission.destination?.lat,
        dLng: mission.destination?.lng,
        rLat: mission.toReturn?.lat,
        rLng: mission.toReturn?.lng,
      }),
    [
      previewType,
      mission.id,
      mission.type,
      mission.status,
      mission.origin?.lat,
      mission.origin?.lng,
      mission.destination?.lat,
      mission.destination?.lng,
      mission.toReturn?.lat,
      mission.toReturn?.lng,
    ]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: `routeEffectKey` captures geometry; `mission` object identity churns each render
  useEffect(() => {
    if (!map) return;
    const renderers: google.maps.Polyline[] = [];
    const bounds = new google.maps.LatLngBounds();
    if (previewType === PreviewType.AB) {
      // we will check if the origin and destination are of valid types eg: LatLngLiteral or LatLng
      if (
        typeof mission.origin?.lat === "undefined" ||
        typeof mission.destination?.lat === "undefined"
      ) {
        return;
      }

      bounds.extend(mission.origin);
      bounds.extend(mission.destination);
      renderers.push(
        addRoutes(map, {
          origin: mission.origin,
          destination: mission.destination,
          index: 2,
        })
      );
      map.fitBounds(bounds);
    } else if (previewType === PreviewType.BC) {
      // we will check if the destination and return are valid
      if (
        typeof mission.destination.lat === "undefined" ||
        typeof mission.toReturn.lat === "undefined"
      ) {
        return;
      }
      bounds.extend(mission.destination);
      if (mission.toReturn) {
        bounds.extend(mission.toReturn);
      }
      renderers.push(
        addRoutes(map, {
          color: getDeliveryTypeColor(mission.type),
          origin: mission.destination,
          destination: mission.toReturn,
          weight: 2,
          index: 1,
        })
      );
      map.fitBounds(bounds);
    } else if (previewType === PreviewType.ABC) {
      // we will check if the origin, destination and return are valid
      if (
        typeof mission.origin.lat === "undefined" ||
        typeof mission.destination.lat === "undefined" ||
        typeof mission.toReturn.lat === "undefined"
      ) {
        return;
      }
      const datas = [
        {
          color: getDeliveryTypeColor(mission.type),
          origin: mission.destination,
          destination: mission.toReturn,
          weight: 2,
          index: 1,
        },
        {
          origin: mission.origin,
          destination: mission.destination,
          index: 2,
        },
      ];
      const bounds = new google.maps.LatLngBounds();
      datas.forEach((data) => {
        renderers.push(addRoutes(map, data));
        bounds.extend(data.origin);
        bounds.extend(data.destination);
      });
      map.fitBounds(bounds);
    }

    // Cleanup: remove old route renderers when coordinates change or component unmounts
    return () => {
      for (const renderer of renderers) {
        renderer.setMap(null);
      }
    };
  }, [map, routeEffectKey]);

  return (
    <>
      <CustomMarker
        key={`${mission.id}destination-marker`}
        mission={mission}
        title={
          <h5
            className="marker-title"
            style={{
              whiteSpace: "normal",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span
              style={{
                textDecoration: `underline ${getDeliveryTypeColor(mission.type)}`,
                textUnderlineOffset: "2px",
                textTransform: "capitalize",
              }}
            >
              {previewType === PreviewType.BC
                ? t("common.shared.departure-point")
                : t("common.shared.arrival-point")}
            </span>
            <span
              style={{
                width: 13,
                height: 13,
                background: getDeliveryTypeColor(mission.type),
                display: "block",
                borderRadius: "50%",
              }}
            ></span>
          </h5>
        }
        description={`${mission.destinationCity}`}
        position={mission.destination}
        extendable={mission.status === DeliveryStatus.ONGOING}
      >
        <motion.div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            position: "relative",
            zIndex: 99,
          }}
          initial={{
            opacity: 0,
          }}
          animate={{
            opacity: 1,
          }}
          exit={{
            opacity: 0,
          }}
          transition={{
            delay: 0.47,
          }}
        >
          <Typography sx={{ color: "white", fontSize: 12, fontStyle: "normal" }} variant="body1">
            {mission.arrive}
          </Typography>
        </motion.div>
      </CustomMarker>
      {/* origin marker */}

      {previewType !== PreviewType.BC && mission.origin?.lat ? (
        <CustomMarker
          extraStyle={{
            bgColor: "#3B3C70",
          }}
          key={`${mission.id}origin-marker`}
          mission={mission}
          title={
            <h5
              className="marker-title"
              style={{
                whiteSpace: "normal",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span style={{ textTransform: "capitalize" }}>
                {t("common.shared.departure-point")}
              </span>
            </h5>
          }
          description={`${mission.originCity}`}
          position={mission.origin}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            <Typography sx={{ color: "white", fontSize: 12, fontStyle: "normal" }} variant="body1">
              {mission.depart}
            </Typography>
          </div>
        </CustomMarker>
      ) : null}
      {/* restitution */}
      {previewType !== PreviewType.AB && mission.toReturn?.lat ? (
        <CustomMarker
          extraStyle={{
            bgColor: "#28293d",
          }}
          key={`${mission.id}restitution-marker`}
          mission={mission}
          title={
            <h5
              className="marker-title"
              style={{
                whiteSpace: "normal",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>{t("common.shared.restitution-point")}</span>
            </h5>
          }
          position={mission.toReturn}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            <Typography sx={{ color: "white", fontSize: 12, fontStyle: "normal" }} variant="body1">
              {mission.restitution}
            </Typography>
          </div>
        </CustomMarker>
      ) : null}
    </>
  );
}

export default PreviewMissionMarkers;
